import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import usePublicBranding from 'hooks/usePublicBranding';
import Loader from 'ui-component/Loader';
import { DASHBOARD_PATH } from 'config';
import TrialExpired from 'views/error/TrialExpired';

// ==============================|| GUEST GUARD ||============================== //

/**
 * Guest guard for routes having no auth required
 * @param {PropTypes.node} children children element/node
 */

const GuestGuard = ({ children }) => {
    const { isLoggedIn, user } = useAuth();
    const { isTrialExpired, isLoading } = usePublicBranding();
    const location = useLocation();
    const isPasswordResetRoute =
        /^\/login\/reset-password\/[^/]+\/?$/.test(location.pathname) ||
        /^\/candidate\/[^/]+\/login\/reset-password\/[^/]+\/?$/.test(location.pathname);

    if (isLoading) return <Loader />;

    // Trial expiry takes precedence over an existing session or a password reset link.
    if (isTrialExpired === true) return <TrialExpired />;

    // A valid email token must remain reachable even when an old login session is still active.
    if (isLoggedIn && !isPasswordResetRoute) {
        const userAssessmentCode = user?.assessmentCode || user?.assessmentId || '';
        const guestRequestCode = user?.requestCode || user?.assessmentId || '';
        const fallbackPath =
            user?.principalType === 'candidate'
                ? userAssessmentCode
                    ? `/candidate/${userAssessmentCode}`
                    : '/login'
                : user?.principalType === 'guest'
                ? guestRequestCode
                    ? `/guest/${guestRequestCode}`
                    : '/login'
                : DASHBOARD_PATH;
        return <Navigate to={fallbackPath} replace />;
    }

    return children;
};

GuestGuard.propTypes = {
    children: PropTypes.node
};

export default GuestGuard;
